import React, { useState } from 'react';
import { useIntl } from '@edx/frontend-platform/i18n';

import messages from '../messages';
import { LtiConfig, updateLtiConfig } from '../data/api';
import EvaluationAnchors from './EvaluationAnchors';

interface EditConfigPanelProps {
  config: LtiConfig;
  onSaved: (updated: LtiConfig[]) => void;
  onClose: () => void;
}

const EditConfigPanel: React.FC<EditConfigPanelProps> = ({
  config,
  onSaved,
  onClose,
}) => {
  const intl = useIntl();
  const isPostAssessment = config.lti_type === 'post_assessment';
  const [skill, setSkill] = useState(config.skill);
  const [scenarioText, setScenarioText] = useState(config.scenario_text);
  const [responsePrompt, setResponsePrompt] = useState(config.response_prompt);
  const [maxAttempts, setMaxAttempts] = useState(config.max_attempts);
  const [rubric, setRubric] = useState(config.rubric.map((d) => ({ ...d })));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingDimensionRemoval, setPendingDimensionRemoval] = useState<number | null>(null);

  const updateDim = (idx: number, field: 'name', val: string) => {
    setRubric((prev) => prev.map((d, i) => (i === idx ? { ...d, [field]: val } : d)));
  };

  const addDimension = () => {
    setRubric((previous) => [
      ...previous,
      {
        name: '',
        weight: 1,
        observable_criteria: [''],
        course_concepts: [{ name: '', definition: '' }],
        source_excerpts: [{ text: '', sequential_id: '', vertical_id: '' }],
      },
    ]);
  };

  const removeDimension = (index: number) => {
    setRubric((previous) => previous.filter((_, itemIndex) => itemIndex !== index));
    setPendingDimensionRemoval(null);
  };

  const handleSave = async () => {
    if (isPostAssessment) { return; }
    setSaving(true);
    setError(null);

    try {
      const updated = await updateLtiConfig(config.config_id, {
        skill,
        scenario_text: scenarioText,
        response_prompt: responsePrompt,
        max_attempts: maxAttempts,
        rubric,
      });
      onSaved(updated);
    } catch {
      setError(intl.formatMessage(messages.editSaveErrorMessage));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card lti-tools-tab__edit-panel">
      <div className="card-header lti-tools-tab__panel-header lti-tools-tab__edit-panel-header">
        <strong>
          ✏️ {intl.formatMessage(messages.editPanelHeading)} — <code>{config.scenario_id}</code>
        </strong>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={onClose}
          disabled={saving}
        >
          {intl.formatMessage(messages.editCancelButton)}
        </button>
      </div>

      <div className="card-body">
        <p className="lti-tools-tab__panel-description lti-tools-tab__panel-description--spacious">
          {intl.formatMessage(messages.editPanelDescription)}
        </p>
        {isPostAssessment && (
          <div className="alert alert-info" role="note">
            {intl.formatMessage(messages.postAssessmentReadOnlyHint)}
          </div>
        )}

        {error && (
          <div className="alert alert-danger alert-dismissible lti-tools-tab__alert">
            {error}
            <button
              type="button"
              className="lti-tools-tab__alert-close"
              onClick={() => setError(null)}
              aria-label="Close"
            >
              ×
            </button>
          </div>
        )}

        <div className="lti-tools-tab__field lti-tools-tab__field--spacious">
          <label className="form-label lti-tools-tab__field-label">
            {intl.formatMessage(messages.editSkillLabel)}
          </label>
          <input
            className="form-control lti-tools-tab__compact-control"
            value={skill}
            onChange={isPostAssessment ? undefined : (event) => setSkill(event.target.value)}
            disabled={saving}
            readOnly={isPostAssessment}
          />
        </div>

        <div className="lti-tools-tab__field lti-tools-tab__field--spacious">
          <label className="form-label lti-tools-tab__field-label">
            {intl.formatMessage(messages.editScenarioTextLabel)}
          </label>
          <textarea
            className="form-control lti-tools-tab__compact-control"
            rows={8}
            value={scenarioText}
            onChange={isPostAssessment ? undefined : (event) => setScenarioText(event.target.value)}
            disabled={saving}
            readOnly={isPostAssessment}
          />
        </div>

        <div className="lti-tools-tab__field lti-tools-tab__field--spacious">
          <label className="form-label lti-tools-tab__field-label">
            {intl.formatMessage(messages.editResponsePromptLabel)}
          </label>
          <input
            className="form-control lti-tools-tab__compact-control"
            value={responsePrompt}
            onChange={isPostAssessment ? undefined : (event) => setResponsePrompt(event.target.value)}
            disabled={saving}
            readOnly={isPostAssessment}
          />
        </div>

        <div className="lti-tools-tab__field lti-tools-tab__field--spacious lti-tools-tab__max-attempts-control">
          <label className="form-label lti-tools-tab__field-label">
            {intl.formatMessage(messages.editMaxAttemptsLabel)}
          </label>
          <input
            type="number"
            min={1}
            max={5}
            className="form-control lti-tools-tab__compact-control"
            value={maxAttempts}
            onChange={isPostAssessment ? undefined : (event) => setMaxAttempts(Number(event.target.value))}
            disabled={saving}
            readOnly={isPostAssessment}
          />
        </div>

        {config.placement_hint && (
          <p className="form-text lti-tools-tab__placement-hint">
            💡<strong> {config.placement_hint}</strong>
          </p>
        )}

        <p className="lti-tools-tab__subheading">{intl.formatMessage(messages.editRubricHeading)}</p>

        {rubric.map((dim, di) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={di} className="lti-tools-tab__rubric-dimension">
            <div className="lti-tools-tab__rubric-dimension-heading">
              <input
                className="form-control lti-tools-tab__compact-control"
                value={dim.name}
                placeholder={intl.formatMessage(messages.editRubricNamePlaceholder)}
                onChange={isPostAssessment ? undefined : (event) => updateDim(di, 'name', event.target.value)}
                disabled={saving}
                readOnly={isPostAssessment}
              />
              {!isPostAssessment && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => setPendingDimensionRemoval(di)}
                  disabled={saving}
                >
                  Remove dimension
                </button>
              )}
            </div>

            {!isPostAssessment && pendingDimensionRemoval === di && (
              <div className="lti-tools-tab__inline-confirmation" role="status">
                Remove this rubric dimension and all of its anchors?
                <button
                  type="button"
                  className="btn btn-sm btn-link"
                  onClick={() => setPendingDimensionRemoval(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-danger"
                  onClick={() => removeDimension(di)}
                >
                  Remove
                </button>
              </div>
            )}

            <EvaluationAnchors
              dimension={dim}
              onChange={(updates) => setRubric((previous) => previous.map((item, index) => (
                index === di ? { ...item, ...updates } : item
              )))}
              disabled={saving}
              readOnly={isPostAssessment}
            />
          </div>
        ))}

        {!isPostAssessment && (
          <button
            type="button"
            className="btn btn-sm btn-outline-primary lti-tools-tab__add-rubric-dimension"
            onClick={addDimension}
            disabled={saving}
          >
            + Add rubric dimension
          </button>
        )}

        {!isPostAssessment && (
          <div className="lti-tools-tab__button-group">
            <button
              type="button"
              className="btn btn-warning"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? (
                <>
                  <span className="spinner-border lti-tools-tab__spinner" role="status" aria-hidden="true" />
                  Saving…
                </>
              ) : (
                intl.formatMessage(messages.editSaveButton)
              )}
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={onClose}
              disabled={saving}
            >
              {intl.formatMessage(messages.editCancelButton)}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditConfigPanel;
